import { useEffect, useRef, useState } from "react";
import { HardHat, Plane, Scale, Cpu, Stethoscope } from "lucide-react";

const HERO_ICONS = {
  engineering: HardHat,
  aviation: Plane,
  healthcare: Stethoscope,
  law: Scale,
  technology: Cpu,
};

/** Full literals so Tailwind sees every class. Slides alternate orange and blue. */
const TONES = [
  { card: "border-brand-200 bg-brand-50", pill: "border-brand-200", bar: "border-brand-200 bg-brand-100/70", pillIcon: "bg-brand-500 text-white", dot: "bg-brand-500" },
  { card: "border-brand-500 bg-brand-100", pill: "border-brand-500", bar: "border-brand-500 bg-brand-200/70", pillIcon: "bg-brand-500 text-white", dot: "bg-brand-500" },
];

const AUTO_MS = 4500;

function prefersReducedMotion() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Hero visual: one teen + mentor cut-out at a time, cross-fading every few seconds.
 * Pauses on mouse hover or keyboard focus, only runs while on screen, and stays still with reduced motion.
 * The dots jump to a slide and restart the timer.
 */
export function HeroCarousel({ slides }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const rootRef = useRef(null);
  const count = slides.length;

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !visible || prefersReducedMotion()) return undefined;
    const id = window.setTimeout(() => setActive((current) => (current + 1) % count), AUTO_MS);
    return () => window.clearTimeout(id);
  }, [active, paused, visible, count]);

  const current = slides[active];
  const tone = TONES[active % TONES.length];

  return (
    <div
      ref={rootRef}
      className="mx-auto w-full max-w-sm sm:max-w-md lg:mr-0 lg:ml-auto"
      onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
      onFocus={(e) => e.target.matches(":focus-visible") && setPaused(true)}
      onBlur={() => setPaused(false)}
      data-testid="home-hero-carousel"
    >
      <div className={`relative h-[26rem] overflow-hidden rounded-5xl border transition-colors duration-500 sm:h-[29rem] lg:h-[clamp(23rem,54vh,30rem)] ${tone.card}`}>
        {/* A soft white spotlight behind whoever is on stage */}
        <span aria-hidden="true" className="absolute bottom-[3.5rem] left-1/2 aspect-square w-[82%] -translate-x-1/2 rounded-full bg-white/70" />

        {slides.map((slide, i) => {
          const on = i === active;
          return (
            <img
              key={slide.id}
              src={`${process.env.PUBLIC_URL}/${slide.file}`}
              alt={slide.alt}
              width={532}
              height={1024}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              aria-hidden={on ? undefined : "true"}
              className={`absolute bottom-[3.5rem] left-1/2 h-[calc(100%-4rem)] w-auto max-w-none -translate-x-1/2 object-contain object-bottom transition-all duration-700 ease-out ${
                on ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
              }`}
            />
          );
        })}

        {/* The field on stage right now */}
        <span
          className={`absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-3 text-xs font-bold leading-none text-ink shadow-soft transition-colors duration-500 sm:left-4 sm:top-4 sm:text-sm ${tone.pill}`}
        >
          <span
            key={current.id}
            className={`flex h-7 w-7 animate-pop items-center justify-center rounded-full ${tone.pillIcon}`}
            aria-hidden="true"
          >
            {(() => {
              const Icon = HERO_ICONS[current.icon] ?? HardHat;
              return <Icon className="h-4 w-4" />;
            })()}
          </span>
          {current.field}
        </span>

        {/* Dots: stacked down the right edge, centred vertically */}
        <div
          className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center rounded-full border border-white/80 bg-white/80 py-1 shadow-soft sm:right-3"
          role="group"
          aria-label="Choose a field"
        >
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show ${slide.field}`}
              aria-pressed={i === active}
              className="group flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            >
              <span
                className={`block w-2.5 rounded-full transition-all duration-300 ${
                  i === active ? `h-6 ${TONES[i % TONES.length].dot}` : "h-2.5 bg-brand-200 group-hover:bg-brand-300"
                }`}
              />
            </button>
          ))}
        </div>

        {/* Caption bar: a "floor" under the people */}
        <div
          className={`absolute inset-x-0 bottom-0 z-10 flex h-14 items-center justify-center border-t-2 px-3 transition-colors duration-500 sm:px-4 ${tone.bar}`}
        >
          <p aria-live="off" className="text-center font-heading text-sm font-bold leading-tight text-ink sm:text-base">
            {current.line}
          </p>
        </div>
      </div>
    </div>
  );
}
